import { useEffect, useState, type JSX, type ReactNode } from 'react';

import { useAuthStore } from '../../state/auth.js';
import { LoginPage } from './LoginPage.js';
import { AdminPanel } from '../../panels/admin/AdminPanel.js';
import { AccountPanel } from '../../panels/account/AccountPanel.js';

const ADMIN_HASH = '#admin';
const ACCOUNT_HASH = '#account';

/**
 * Wraps the app and gates rendering on the auth state:
 *   - while we're checking → spinner placeholder
 *   - anonymous → `<LoginPage>`
 *   - authenticated + hash === '#admin' → `<AdminPanel>` (admin only)
 *   - authenticated + hash === '#account' → `<AccountPanel>` (any user)
 *   - authenticated otherwise → children (the AppShell banner hosts the
 *     `<UserMenu>` inline so it doesn't overlap the capability pills)
 */
export function AuthGate(props: { children: ReactNode }): JSX.Element {
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);
  const refresh = useAuthStore((s) => s.refresh);
  const [hash, setHash] = useState<string>(() =>
    typeof window !== 'undefined' ? window.location.hash : '',
  );
  const [hasInit, setHasInit] = useState(status !== 'idle');

  useEffect(() => {
    // Only refresh on first mount when the store is still in its initial
    // 'idle' state. This makes the gate friendly to tests that pre-seed an
    // authenticated user (otherwise the refresh would race-replace the
    // seeded user with whatever `/auth/me` returns from the test fetcher).
    if (status === 'idle') {
      void refresh().finally(() => setHasInit(true));
    } else if (!hasInit) {
      setHasInit(true);
    }
  }, [refresh, status, hasInit]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    function onHashChange(): void {
      setHash(window.location.hash);
    }
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  if (!hasInit) {
    return (
      <div className="bg-cream-50 text-taupe-700 flex min-h-screen items-center justify-center">
        <span className="text-xs font-semibold uppercase tracking-[0.18em]">
          Loading…
        </span>
      </div>
    );
  }

  if (user === null) {
    // Render LoginPage for both 'anonymous' AND in-flight 'loading' so the
    // form stays mounted while the login API is round-tripping — that
    // preserves the user's typed username/password across a failed attempt.
    return <LoginPage />;
  }

  const showingAdmin = hash === ADMIN_HASH && user.role === 'admin';

  if (showingAdmin) {
    return (
      <AdminPanel
        onClose={() => {
          window.location.hash = '';
        }}
      />
    );
  }

  if (hash === ACCOUNT_HASH) {
    return (
      <AccountPanel
        onClose={() => {
          window.location.hash = '';
        }}
      />
    );
  }

  return <>{props.children}</>;
}
